import { OperationVariables } from '@apollo/client';

import { FetchPolicyKeys } from 'domain/api/graphql/types';

import { DEFAULT_MAX_ATTEMPTS, DEFAULT_POLLING_INTERVAL, DEFAULT_STOP_POLLING_TIMEOUT } from './consts';
import { GenericPollingConfig } from './types';
import { TEXTS } from './texts';

export const delay = (ms: number, signal?: AbortSignal): Promise<void> =>
  new Promise<void>((resolve) => {
    const timerId = setTimeout(resolve, ms);
    signal?.addEventListener('abort', () => {
      clearTimeout(timerId);
      resolve();
    });
  });

export const startGenericPolling = async <TData, TVariables extends OperationVariables>(
  config: GenericPollingConfig<TData, TVariables>,
  signal: AbortSignal
): Promise<void> => {
  const {
    apolloClient,
    query,
    variables,
    isComplete,
    onComplete,
    onError,
    pollingInterval = DEFAULT_POLLING_INTERVAL,
    maxAttempts = DEFAULT_MAX_ATTEMPTS,
    timeout = DEFAULT_STOP_POLLING_TIMEOUT,
    context,
  } = config;

  const pollRecursive = async (attempt: number = 0, startTime: number = Date.now()): Promise<void> => {
    if (signal.aborted) {
      console.log(`🛑 Polling aborted for attempt ${attempt}`);

      return;
    }

    const elapsed = Date.now() - startTime;

    console.log(`🔄 Attempt ${attempt + 1}/${maxAttempts}, elapsed: ${elapsed}ms, timeout: ${timeout}ms`);

    if (elapsed > timeout) {
      console.log(`⏰ Polling timeout after ${elapsed}ms (max: ${timeout}ms)`);
      onError(new Error(TEXTS.POLLING_TIMEOUT));

      return;
    }

    if (attempt >= maxAttempts) {
      console.log(`🚫 Maximum polling attempts reached: ${attempt}/${maxAttempts}`);
      onError(new Error(TEXTS.POLLING_TOO_MANY_ATTEMPTS));

      return;
    }

    try {
      console.log(`📡 Making query attempt ${attempt + 1}...`);

      const { data } = await apolloClient.query<TData, TVariables>({
        query,
        variables,
        fetchPolicy: FetchPolicyKeys.NETWORK_ONLY,
        context: { fetchOptions: { signal }, ...context },
      });

      console.log(`✅ Query attempt ${attempt + 1} completed`);

      if (isComplete(data)) {
        onComplete(data);

        return;
      }

      console.log(`⏭️  Data not complete, waiting ${pollingInterval}ms for next attempt...`);

      await delay(pollingInterval, signal);

      if (!signal.aborted) {
        console.log(`➡️  Proceeding to next attempt...`);

        await pollRecursive(attempt + 1, startTime);
      } else {
        console.log(`🛑 Polling aborted during delay`);
      }
    } catch (error) {
      if (signal.aborted) {
        console.log(`🛑 Polling aborted during error handling`);
        return;
      }

      onError(error instanceof Error ? error : new Error('Unknown polling error'));

      const backoffDelay = Math.min(pollingInterval * 2 ** attempt, 30000);

      await delay(backoffDelay, signal);

      if (!signal.aborted) {
        await pollRecursive(attempt + 1, startTime);
      }
    }
  };

  console.log(`🚀 Starting polling with max ${maxAttempts} attempts, timeout: ${timeout}ms`);
  await pollRecursive();
};
